import { ConfigConsumer } from 'antd/lib/config-provider'
import type {
  GroupProps as AntdGroupProps,
  InputProps as AntdInputProps,
  TextAreaProps as AntdTextAreaProps,
  PasswordProps as AntdPasswordProps,
  SearchProps as AntdSearchProps,
} from 'antd/lib/input'
import type { default as AntdPassword } from 'antd/lib/input/Password'
import type { default as AntdTextArea } from 'antd/lib/input/TextArea'
import type { default as AntdGroup } from 'antd/lib/input/Group'
import type { default as AntdSearch } from 'antd/lib/input/Search'
import { default as AntdInput } from 'antd/lib/input'
import classNames from 'classnames'
import { connectReadonlyComponent } from '../connect'

import React, { forwardRef } from 'react'
import PreviewText from '../preview-text'

export interface InputProps extends AntdInputProps {
  // 线框风格
  borderStyle?: 'outline'
  readonly?: boolean
}

export interface GroupProps extends AntdGroupProps {
  // 线框风格
  borderStyle?: 'outline'
}

export interface TextAreaProps extends AntdTextAreaProps {
  // 线框风格
  borderStyle?: 'outline'
  readonly?: boolean
}

export interface PasswordProps extends AntdPasswordProps {
  // 线框风格
  borderStyle?: 'outline'
  readonly?: boolean
}

export interface SearchProps extends AntdSearchProps {
  // 线框风格
  borderStyle?: 'outline'
  readonly?: boolean
}

const Group = forwardRef<typeof AntdGroup, GroupProps>(
  ({ className, borderStyle, ...props }, ref) => (
    <ConfigConsumer>
      {({ getPrefixCls }) => {
        const prefixCls = getPrefixCls('input')

        return (
          <AntdInput.Group
            {...props}
            className={classNames(className, {
              [`${prefixCls}-outline`]: borderStyle === 'outline',
            })}
            // @ts-ignore
            ref={ref}
          />
        )
      }}
    </ConfigConsumer>
  )
)

const Search = forwardRef<AntdSearch, SearchProps>(
  ({ className, borderStyle, readonly, ...props }, ref) =>
    readonly ? (
      <PreviewText.Input {...props} />
    ) : (
      <ConfigConsumer>
        {({ getPrefixCls }) => {
          const prefixCls = getPrefixCls('input')

          return (
            <AntdInput.Search
              {...props}
              className={classNames(className, {
                [`${prefixCls}-outline`]: borderStyle === 'outline',
              })}
              ref={ref}
            />
          )
        }}
      </ConfigConsumer>
    )
)

const TextArea = forwardRef<AntdTextArea, TextAreaProps>(
  ({ className, borderStyle, readonly, ...props }, ref) =>
    readonly ? (
      // @ts-ignore
      <PreviewText.Input {...props} />
    ) : (
      <ConfigConsumer>
        {({ getPrefixCls }) => {
          const prefixCls = getPrefixCls('input')

          return (
            <AntdInput.TextArea
              {...props}
              className={classNames(className, {
                [`${prefixCls}-outline`]: borderStyle === 'outline',
              })}
              ref={ref}
            />
          )
        }}
      </ConfigConsumer>
    )
)

const Password = forwardRef<AntdPassword, PasswordProps>(
  ({ className, borderStyle, readonly, ...props }, ref) =>
    readonly ? (
      <PreviewText.Input {...props} />
    ) : (
      <ConfigConsumer>
        {({ getPrefixCls }) => {
          const prefixCls = getPrefixCls('input')

          return (
            <AntdInput.Password
              {...props}
              ref={ref}
              className={classNames(className, {
                [`${prefixCls}-outline`]: borderStyle === 'outline',
              })}
            />
          )
        }}
      </ConfigConsumer>
    )
)

interface InputComponent
  extends React.ForwardRefExoticComponent<InputProps & React.RefAttributes<AntdInput>> {
  Group: typeof Group
  TextArea: typeof TextArea
  Password: typeof Password
  Search: typeof Search
}

export const InternalInput = forwardRef<AntdInput, InputProps>(
  ({ className, borderStyle, ...props }, ref) => (
    <ConfigConsumer>
      {({ getPrefixCls }) => {
        const prefixCls = getPrefixCls('input')

        return (
          <AntdInput
            {...props}
            ref={ref}
            className={classNames(className, {
              [`${prefixCls}-outline`]: borderStyle === 'outline',
            })}
          />
        )
      }}
    </ConfigConsumer>
  )
) as InputComponent

export const Input = connectReadonlyComponent(InternalInput, PreviewText.Input) as InputComponent

Input.displayName = 'Input'

Input.Group = Group
Input.TextArea = TextArea
Input.Password = Password
Input.Search = Search
